import React from 'react';

/**
 * AADefenseEffect - CSS-based AA firing animation
 * Shows country-specific AA missiles firing at incoming bombers with rotation
 * NOW AIMS AT PLANE POSITION with missiles pointing tip-first + black smoke flak
 */
export default function AADefenseEffect({ onComplete, targetCountryPosition, aaPositions, defendingCountry, aaTier }) {
  React.useEffect(() => {
    // Auto-complete after 2.5 seconds (duration of effect)
    const timer = setTimeout(() => {
      onComplete();
    }, 2500);

    return () => clearTimeout(timer);
  }, [onComplete]);

  // Default plane position (top center) if not provided
  const planeX = targetCountryPosition?.x || 50; // % from left
  const planeY = targetCountryPosition?.y || 20; // % from top

  // Default AA positions (bottom third of screen, spread out) if not provided
  const defaultAAPositions = [
    { x: 20, y: 75 },
    { x: 40, y: 80 },
    { x: 60, y: 78 },
    { x: 80, y: 76 },
  ];
  const guns = aaPositions || defaultAAPositions;

  // Country-specific AA missile sprites
  const AA_MISSILES = {
    'USA': '/assets/aa-missiles/stinger.png',
    'RUS': '/assets/aa-missiles/strela-2.png',
    'CHN': '/assets/aa-missiles/fn-6.png',
    'NK': '/assets/aa-missiles/ht-16pgj.png',
    'EU': '/assets/aa-missiles/mistral.png'
  };
  
  const missileSprite = AA_MISSILES[defendingCountry] || AA_MISSILES['USA'];
  
  // Debug logging
  console.log('🚀 AADefenseEffect rendering:', {
    defendingCountry,
    aaTier,
    missileSprite,
    missilesToFire: Math.max(2, (aaTier || 0) * 2),
    planePosition: { x: planeX, y: planeY }
  });
  
  // Number of missiles based on AA tier
  // Tier 0 (no AA purchased) = 2 missiles (base defense)
  // Tier 1 = 2 missiles, Tier 2 = 4, Tier 3 = 6
  const missilesToFire = Math.max(2, (aaTier || 0) * 2);
  const activeGuns = guns.slice(0, missilesToFire);

  return (
    <div className="fixed inset-0 z-50 pointer-events-none">
      {/* AA Missiles firing AT the plane - Country-specific sprites with rotation */}
      {activeGuns.map((gun, i) => {
        // Calculate angle from AA gun to plane (for tip-first rotation)
        const deltaX = planeX - gun.x;
        const deltaY = planeY - gun.y;
        const angleRad = Math.atan2(deltaY, deltaX);
        const angleDeg = angleRad * (180 / Math.PI);
        
        // Check if firing left (flip horizontally)
        const isFiringLeft = deltaX < 0;

        return (
          <div key={`aa-missile-${i}`} className="relative" style={{ zIndex: 100 }}>
            {/* AA Missile sprite - rotated tip-first toward plane */}
            <img
              src={missileSprite}
              alt="AA Missile"
              className="absolute"
              data-gun-index={i}
              data-angle={angleDeg}
              data-flip={isFiringLeft ? -1 : 1}
              style={{
                left: `${gun.x}%`,
                top: `${gun.y}%`,
                width: '80px',
                height: 'auto',
                transformOrigin: 'center center',
                transform: `translate(-50%, -50%) rotate(${angleDeg}deg) scaleX(${isFiringLeft ? -1 : 1})`,
                filter: 'drop-shadow(0 0 20px rgba(255,140,0,1)) brightness(1.2)',
                animation: `missileFlight-${i} 1.0s ease-out ${i * 0.15}s forwards`,
                pointerEvents: 'none',
                zIndex: 100,
              }}
            />
          </div>
        );
      })}

      {/* Black Smoke Flak Bursts AROUND THE PLANE - Only for AA Tier 1-3 */}
      {aaTier && aaTier >= 1 && [...Array(Math.min(8, aaTier * 3))].map((_, i) => {
        // Position flak bursts randomly AROUND the plane's current position
        const offsetX = (Math.random() - 0.5) * 8;  // ±4% around plane
        const offsetY = (Math.random() - 0.5) * 8;
        
        return (
          <div
            key={`flak-${i}`}
            className="absolute rounded-full"
            style={{
              left: `${planeX + offsetX}%`,
              top: `${planeY + offsetY}%`,
              width: '40px',
              height: '40px',
              background: 'radial-gradient(circle, rgba(40,40,40,0.95) 0%, rgba(60,60,60,0.7) 40%, transparent 70%)',
              boxShadow: '0 0 25px 10px rgba(30,30,30,0.6)',
              animation: `flakBurst 0.8s ease-out ${i * 0.1}s`,
              pointerEvents: 'none',
              zIndex: 50,  // Below missiles (100) but above background
            }}
          />
        );
      })}

      {/* CSS Animations */}
      <style>{`
        /* Individual missile flight animations - each missile flies to the plane and despawns on contact */
        ${activeGuns.map((gun, i) => {
          // Calculate angle and flip for this missile
          const deltaX = planeX - gun.x;
          const deltaY = planeY - gun.y;
          const angleRad = Math.atan2(deltaY, deltaX);
          const angleDeg = angleRad * (180 / Math.PI);
          const isFiringLeft = deltaX < 0;
          const flipScale = isFiringLeft ? -1 : 1;
          
          return `
            @keyframes missileFlight-${i} {
              0% {
                left: ${gun.x}%;
                top: ${gun.y}%;
                opacity: 1;
                transform: translate(-50%, -50%) rotate(${angleDeg}deg) scaleX(${flipScale}) scale(0.3);
              }
              15% {
                left: ${gun.x + deltaX * 0.15}%;
                top: ${gun.y + deltaY * 0.15}%;
                opacity: 1;
                transform: translate(-50%, -50%) rotate(${angleDeg}deg) scaleX(${flipScale}) scale(1);
              }
              92% {
                left: ${planeX}%;
                top: ${planeY}%;
                opacity: 1;
                transform: translate(-50%, -50%) rotate(${angleDeg}deg) scaleX(${flipScale}) scale(1);
              }
              100% {
                left: ${planeX}%;
                top: ${planeY}%;
                opacity: 0;
                transform: translate(-50%, -50%) rotate(${angleDeg}deg) scaleX(${flipScale}) scale(0.2);
              }
            }
          `;
        }).join('\n')}

        @keyframes flakBurst {
          0% {
            opacity: 0;
            transform: scale(0.2);
          }
          30% {
            opacity: 0.9;
            transform: scale(1.2);
          }
          100% {
            opacity: 0;
            transform: scale(2);
          }
        }

        @keyframes planeShake {
          0%, 100% {
            transform: translate(-50%, -50%) rotate(-2deg);
          }
          50% {
            transform: translate(-50%, -50%) rotate(2deg);
          }
        }
      `}</style>
    </div>
  );
}
